import type { ZudokuConfig } from "zudoku";
import { ZudokuPlugin, ZudokuEvents } from "zudoku";

import { LandingHero } from "./components/LandingHero.js";

import { APIS, DESCRIPTION, docPagePath, SITE_ORIGIN, tagDefinitions } from "./src/registry.js";
import "./styles/custom.css";

const LOGO_URL = `${SITE_ORIGIN}/img/spoke_logo.png`;

// Carried over from the retired Redoc page (src/index.hbs).
const JSON_LD = JSON.stringify({
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "Organization",
      "@id": "https://www.getspoke.com/#organization",
      name: "Spoke Phone",
      url: "https://www.getspoke.com",
      logo: LOGO_URL,
      sameAs: ["https://x.com/SpokePhone"],
    },
    {
      "@type": "WebSite",
      "@id": `${SITE_ORIGIN}/#website`,
      name: "Spoke Developer API",
      url: SITE_ORIGIN,
      description: DESCRIPTION,
      publisher: { "@id": "https://www.getspoke.com/#organization" },
      inLanguage: "en",
    },
    {
      "@type": "APIReference",
      "@id": `${SITE_ORIGIN}/#apiref`,
      name: "Spoke API Reference",
      headline: "Spoke Developer API Reference",
      url: SITE_ORIGIN,
      description:
        "Reference documentation for the Spoke Developer API. Connect Spoke to your business systems: " +
        "query call logs and call data, send and manage SMS and WhatsApp conversations, sync CRM contacts " +
        "into the Spoke Unified Directory for enhanced caller ID, track real-time user availability, run AI " +
        "content analysis on call recordings and transcripts, overlay SIP trunks onto legacy PBX systems, " +
        "customise call flows with data actions, and subscribe to webhook events.",
      about: "Programmable voice, SMS and WhatsApp communications for business",
      programmingModel: "REST",
      termsOfService: "https://www.getspoke.com/legal/terms-of-use",
      keywords: [
        "voice API", "SMS API", "WhatsApp API", "call logs API",
        "conversation intelligence", "SIP trunking", "PBX overlay",
        "CRM integration", "unified directory", "webhooks",
      ],
      image: LOGO_URL,
      isPartOf: { "@id": `${SITE_ORIGIN}/#website` },
      publisher: { "@id": "https://www.getspoke.com/#organization" },
      inLanguage: "en",
    },
  ],
});

declare global {
  interface Window {
    // typescript doesn't know about dataLayer, so we need to declare it
    dataLayer: Record<string, any>[];
  }
}

const googleTagManagerPlugin: ZudokuPlugin = {
  getHead: () => {
    return (
      <script>
        {`
    (function (w, d, s, l, i) {
      w[l] = w[l] || [];
      w[l].push({ "gtm.start": new Date().getTime(), event: "gtm.js" });
      var f = d.getElementsByTagName(s)[0],
        j = d.createElement(s),
        dl = l != "dataLayer" ? "&l=" + l : "";
      j.async = true;
      j.src = "https://www.googletagmanager.com/gtm.js?id=" + i + dl;
      f.parentNode.insertBefore(j, f);
    })(window, document, "script", "dataLayer", "GTM-KHQC6ZLC");`}
      </script>
    );
  },
  events: {
    location: ({ from, to }) => {
      if (!from) return;
      window.dataLayer.push({
        event: "page_view",
        page_path: to.pathname,
        page_title: document.title,
        page_location: window.location.href,
      });
    },
  },
};

const config: ZudokuConfig = {
  site: {
    title: "Spoke Developer API",
    logo: {
      src: { light: "/spoke-logo.svg", dark: "/spoke-logo.svg" },
      alt: "Spoke",
      width: "160px",
    },
    footer: {
      logo: {
        src: { light: "/spoke-logo.svg", dark: "/spoke-logo.svg" },
        alt: "Spoke",
        width: "160px",
      },
      copyright: `© ${new Date().getFullYear()} Spoke Network Limited. All rights reserved.`,
      position: "center",
      columns:[
        {
          title: "Product",
          position: "start",
          links: [
            {
              label: "Spoke Phone",
              href: "https://www.getspoke.com/products/spoke-phone",
            },
            {
              label: "Spoke Enlighten",
              href: "https://www.getspoke.com/products/spoke-enlighten",
            }
          ]
        },
        {
          title: "Company",
          position: "center",
          links: [
            {
              label: "About Us",
              href: "https://www.getspoke.com/about",
            },
            {
              label: "Contact Us",
              href: "https://www.getspoke.com/contact",
            }
          ]
        },
        {
          title: "Legal",
          position: "end",
          links: [
            {
              label: "Privacy Policy",
              href: "https://www.getspoke.com/legal/privacy-policy",
            },
            {
              label: "Terms of Use",
              href: "https://www.getspoke.com/legal/terms-of-use",
            }
          ]
        }
      ]
    }
  },
  canonicalUrlOrigin: SITE_ORIGIN,
  metadata: {
    title: "%s | Spoke Developer API",
    defaultTitle: "Spoke Developer API",
    description: DESCRIPTION,
    favicon: "/img/favicon.ico",
  },
  theme: {
    light: { primary: "#f83f2a", primaryForeground: "#ffffff" },
    dark: { primary: "#f83f2a", primaryForeground: "#ffffff" },
  },
  search: { type: "pagefind" },
  // `http` is not in Zudoku's default language set, and the request/response examples in
  // api-guide.md and core-concepts.md use it.
  syntaxHighlighting: { languages: ["http"] },
  sitemap: {
    siteUrl: SITE_ORIGIN,
    // Zudoku's ~schemas pages are 97% markup and duplicate every operation page.
    exclude: APIS.map((api) => `${api.mount}/~schemas`),
  },
  navigation: [
    // `display: "hide"` keeps the root out of the top-level tabs that root-level
    // entries otherwise produce; `layout: "default"` keeps the header, search and
    // footer around it.
    {
      type: "custom-page" as const,
      path: "/",
      display: "hide" as const,
      layout: "default" as const,
      element: <LandingHero />,
    },
    {
      type: "category",
      label: "Guides",
      items: [
        // `path` remaps the file-based /index route, which the landing page now holds.
        { type: "doc" as const, file: "index", path: "/introduction", label: "Introduction" },
        "authentication",
        "api-guide",
        "core-concepts",
        "passthrough-parameters",
        "routing-twilio-calls-into-spoke",
        "sample-integrations",
        "webhook-events",
        "data-action-concepts",
        "changelog",
      ],
    },
    {
      type: "category",
      label: "Deep Linking",
      items: tagDefinitions
        .filter((tag) => tag.docPage?.category === "Deep Linking")
        .map((tag) => docPagePath(tag).slice(1)),
    },
    ...APIS.map((api) => ({ type: "link" as const, to: api.mount, label: api.navLabel })),
  ],
  // The ?doc= param selects the derivation in zudoku.build.ts. Schema download is off
  // because these documents are display-only, and its URL would embed the date-based
  // info.version, changing on every build. The canonical spec is published unchanged
  // at /spoke-developer-api.json instead.
  apis: APIS.map((api) => ({
    type: "file" as const,
    input: `./build/spoke-developer-api.json?doc=${api.id}`,
    path: api.mount,
    options: {
      schemaDownload: { enabled: false },
    },
  })),
  plugins: [googleTagManagerPlugin]
};

export default config;
